import { io } from 'socket.io-client'
import { jclone } from '@/etc/utils'
import { match } from 'path-to-regexp'
import { getStorage } from '@/etc/storage'

import { LOG } from '@/etc/log'
const log = LOG.m('store::io')

const { VUE_APP_STORAGE, VUE_APP_STORAGE_APP } = process.env

export default {
  namespaced: true,
  state: {
    initialized: false,
    _sockets: {},
    _managers: {},
    _connects: [],
    _io: {},
    io: {},
    connections: {},
  },

  mutations: {
    initialized: (state, next) => {
      state.initialized = next
    },
    add: (state, { code, socket, url, options, binds, open = false }) => {
      if (!state._io) state._io = {}
      if (!state.io) state.io = {}
      state._io[code] = socket
      state.io[code] = {
        code,
        url,
        options,
        binds,
        open,
      }
    },
    update: (state, { code, updates = {} }) => {
      const last = jclone(state.io[code] || {})
      state.io[code] = {
        ...last,
        ...updates,
      }
    },
    connect: (state, code) => {
      if (state.io[code].reconnectAttempts) {
        state.io[code].reconnects++
        state.io[code].reconnectAttempts = 0
      }
      state.io[code].open = true
    },
    disconnect: (state, { code, reason }) => {
      // state.io[code].reconnectAttempts = 0
      if (['io client disconnect', 'io server disconnect'].includes(reason)) {
        state.io[code].open = false
      }
    },
    connect_error: (state, code) => {
      state.io[code].reconnectAttempts++
    },
    updateQuery: (state, { code, jwt }) => {
      const sock = state._io[code]
      const sstt = state.io[code]
      if (!sock || !sstt) {
        log.err('updateQuery error', code, { sock, sstt })
        return
      }
      // log.info('updateQuery', code, jwt, sstt)
      sock.io.opts.query.R = sstt.reconnects || 0
      sock.io.opts.query.RA = sstt.reconnectAttempts
      sock.io.opts.query.jwt = jwt
      // ((store.rootState.user || {}).jwt || sstt.options.jwt || '')
    },
    connects: (state, next) => {
      state._connects = next
    },
    setStateKey: (state, { key, value }) => {
      state[key] = value
    },
  },

  actions: {
    rebuild: (store) => {
      log.info('rebuild!')
      Object.keys(store.state.connections || {}).forEach((mCode) => {
        const mConf = store.state.connections[mCode]
        log.info('socket config', mConf)
      })
    },
    open: async (
      store,
      {
        code,
        url,
        options,
        path = '/vbapi/socket.io/',
        open = true,
        binds = null,
      }
    ) => {
      if (store.state._io[code]) return store.state._io[code]
      const socket = io(url, {
        ...(options || {}),
        path,
        transports: ['websocket'],
        autoConnect: false,
        query: {
          R: 0,
          RA: 0,
          jwt: (store.rootState.user || {}).jwt || options.jwt || '',
        },
      })
      store.commit('add', {
        code,
        socket,
        url,
        options,
        binds,
        open,
      })
      socket.on('connect', () => {
        store.commit('connect', code)
        store.dispatch('saveStorage')
        store.state._connects.forEach((conn) => {
          if (typeof conn === 'function') conn(true)
        })
        store.commit('connects', [])
        if (binds && binds.connect)
          store.dispatch(binds.connect, null, { root: true })
      })
      socket.on('disconnect', (reason) => {
        store.commit('disconnect', { code, reason })
        store.commit('updateQuery', {
          code,
          jwt: (store.rootState.user || {}).jwt || (options || {}).jwt || '',
        })
        store.dispatch('saveStorage')
        store.state._connects.forEach((conn) => {
          if (typeof conn === 'function') conn(false)
        })
        store.commit('connects', [])
        if (binds && binds.disconnect)
          store.dispatch(binds.disconnect, null, { root: true })
        if (['io client disconnect', 'io server disconnect'].includes(reason)) {
          // socket.offAny()
          log.info('closed', code)
          return
        }
        log.err('io disconnected', code, reason)
      })
      socket.on('connect_error', (err) => {
        console.error('io.connect_error', err)
        store.commit('connect_error', code)
        store.commit('update', { code, updates: { connected: false } })
        store.commit('updateQuery', {
          code,
          jwt: (store.rootState.user || {}).jwt || (options || {}).jwt || '',
        })
        store.dispatch('saveStorage')
        store.state._connects.forEach((conn) => {
          if (typeof conn === 'function') conn(false)
        })
        store.commit('connects', [])
        if (binds && binds.connect_error)
          store.dispatch(binds.connect_error, err, null, { root: true })
        log.err('io connect error', code, err)
      })
      if (binds) {
        socket.onAny(async (e, data) => {
          const paths = Object.keys(binds || {})
          log.debug('@any', e, data)
          const route = paths.reduce((acc, p) => {
            if (acc) return acc
            const rx = match(p)
            const result = rx(e, { decode: decodeURIComponent })
            log.debug('@any:rx', p, result)
            if (!result || typeof result !== 'object') return acc
            return {
              template: p,
              ...result,
            }
          }, null)
          if (!route) {
            log.warn('unbinded event', e, data)
          } else {
            await store.dispatch(
              binds[route.template],
              { data, route },
              { root: true }
            )
            log.debug('binded event', e, data, route)
          }
        })
      }
      if (open) socket.connect()
      return socket
    },
    connect: (store, { code }) =>
      new Promise((resolve) => {
        const socket = store.state._io[code]
        if (!socket) return resolve(false)
        if (socket.connected) return resolve(true)
        store.commit('connects', [...store.state._connects, resolve])
      }),
    close: (store, { code }) => {
      if (!store.state._io[code]) return
      store.state._io[code].disconnect()
      log.log('closed', code)
    },

    // load storage method
    loadStorage(store) {
      // const save = super.loadStorage(store)
      const storage = VUE_APP_STORAGE
      const app = VUE_APP_STORAGE_APP
      const scope = 'vyby.io'
      const filter = ['initialized']
      const { fromCache, fromSession } = getStorage(app)
      const cache = fromCache(scope)
      const sess = fromSession(scope)
      const save = sess || cache
      log.debug('load', { cache, sess, save })
      if (!save) return null
      Object.keys(store.state).forEach((key) => {
        if (key.startsWith('_') || filter.includes(key)) return
        log.debug('set state key', key, save[key])
        store.commit('setStateKey', { key, value: save[key] })
      })
      log.debug('loaded:', `<${storage}>`)
      store.dispatch('rebuild')
      return save
    },

    // save storage method
    saveStorage: (store) => {
      const storage = VUE_APP_STORAGE
      const app = VUE_APP_STORAGE_APP
      const scope = 'vyby.io'
      const filter = ['initialized']
      const snap = Object.keys(store.state).reduce((acc, key) => {
        if (
          !key.startsWith('_') &&
          !filter.includes(key) &&
          typeof store.state[key] !== 'undefined'
        )
          acc[key] = jclone(store.state[key])
        return acc
      }, {})
      const { toCache, toSession, delCache, delSession } = getStorage(app)
      if (storage === 'session') {
        toSession(scope, snap)
        delCache(scope)
        log.debug('saved to <session>')
      } else {
        toCache(scope, snap)
        delSession(scope)
        log.debug('saved to <cache>')
      }
      return storage
    },

    init: async (store, initData) => {
      if (store.state.initialized) return
      store.dispatch('loadStorage')
      store.commit('initialized', true)
      log.info('init!', initData)
      if (initData && initData.sockets) {
        await Promise.all(
          Object.keys(initData.sockets || {}).map((code) =>
            store.dispatch('open', { code, ...initData.sockets[code] })
          )
        )
        log.success(
          'sockets open',
          Object.keys(initData.sockets)
            .map((x) => `<${x}>`)
            .join(', ')
        )
      }
    },

    // override deinit to close sockets
    deinit: async (store) => {
      await Promise.all(
        Object.keys(store.state._io).map((s) =>
          store.dispatch('close', { code: s })
        )
      )
      log.info('deinited')
    },
  },
}
